// @flow

import { useEffect, useState } from "react";
import { useExerciseContext } from "../../context/ExerciseContext";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { useAssignmentContext } from "../../context/AssignmentContext";
import { useSubmissionContext } from "../../context/SubmissionContext";
import { StaleListNotice } from "../StaleListNotice";
import Tooltip from "@kea-mod/common/tooltip";
import SubmissionExerciseDataService from "../../axios/services/submission-exercises.service";
export const AssessmentComponent = () => {
  const { t } = useTranslation();
  let { id } = useParams(); //assignmentId
  const navigate = useNavigate();
  const exerciseContext = useExerciseContext();
  const assignmentContext = useAssignmentContext();
  const submissionContext = useSubmissionContext();
  // Per exercise: how many submissions, how many of them assessed, and the mean share of
  // the maximum score. The request that used to only count them now brings the assessments.
  const [exerciseStats, setExerciseStats] = useState<any[]>([]);

  useEffect(() => {
    exerciseContext.all(); //update all exercises in context
    assignmentContext.updateAssignments();
    submissionContext.getAllSubmissions(parseInt(id as string));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    exerciseContext.filterExercises(parseInt(id as string)); //filter all exercises that are in the current assignment

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseContext.exercises]);

  useEffect(() => {
    const exercisesInAssignment = exerciseContext.exercisesInAssignment;

    // Multiple choice results are stored as points, every other assessment stores the
    // score as a factor of the maximum. Dividing a factor by the maximum would report a
    // tenth of the real value on a ten point exercise.
    const shareOfMaximum = (assessment: any, exerciseType: string) => {
      const score = parseFloat(assessment?.totalscore);
      if (isNaN(score)) return null;
      if (exerciseType !== "understanding") return score * 100;
      const max = parseFloat(assessment?.maxTotalScore);
      if (isNaN(max) || max <= 0) return null;
      return (score / max) * 100;
    };

    const collectStats = async () => {
      const collected = [] as any[];
      for (const exercise of exercisesInAssignment) {
        try {
          const response = await SubmissionExerciseDataService.getSubmissionsWithAssessment(exercise.id);
          const submissions = response?.data?.data ?? [];
          const shares = submissions
            .map((submission: any) => shareOfMaximum((submission.assessment ?? [])[0], exercise.type))
            .filter((value: number | null) => value !== null) as number[];
          collected.push({
            submissions: submissions.length,
            assessed: shares.length,
            average: shares.length > 0 ? shares.reduce((sum, value) => sum + value, 0) / shares.length : null,
          });
        } catch (error) {
          collected.push(null);
        }
      }
      setExerciseStats(collected);
    };

    collectStats();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseContext.exercisesInAssignment]);

  function getExerciseTableItem(exercise: any, index: any) {
    return (
      <tr key={"Exercise" + exercise.id}>
        <th>{exercise.name}</th>
        <th>{t(exercise.type)}</th>
        <th>{t(exercise.language.name)}</th>
        {exerciseStats.length > 0 ? (
          <>
            <th className="has-text-right">{exerciseStats[index]?.submissions ?? ""}</th>
            <th className="has-text-right">{exerciseStats[index]?.assessed ?? ""}</th>
            <th className="has-text-right">
              {exerciseStats[index]?.average === null || exerciseStats[index] === undefined
                ? ""
                : exerciseStats[index].average.toFixed(1) + " %"}
            </th>
          </>
        ) : (
          <>
            <td>{"loading..."}</td>
            <td></td>
            <td></td>
          </>
        )}
        <td>
          <button className="button is-success" onClick={() => navigate("/submissions/exercise/" + exercise.id)}>
            {t("submissions")}
          </button>
        </td>
      </tr>
    );
  }

  function getExerciseTable() {
    return (
      <div className="table-container">
        <table className="table is-striped is-narrow is-hoverable is-fullwidth">
          <thead className="is-italic">
            <tr>
              <td>{t("name")}</td>
              <td>{t("type")}</td>
              <td>{t("modellinglang")}</td>
              <td className="has-text-right">{t("numberOfSubmissions")}</td>
              <td className="has-text-right">{t("numberAssessed")}</td>
              <td className="has-text-right">{t("averageShare")}</td>
              <td>{t("options")}</td>
            </tr>
          </thead>
          <tbody>
            {exerciseContext.exercisesInAssignment.map((exercise: any, index: any) => {
              return getExerciseTableItem(exercise, index);
            })}
          </tbody>
        </table>
      </div>
    );
  }

  function getEmptyMessageExercises() {
    if (exerciseContext.exercisesLoadFailed) {
      return <div className="notification is-danger is-light">{t("listLoadFailed")}</div>;
    }
    return <p className="is-size-7">{t("noExercises")} </p>;
  }

  let renderExerciseTable = (
    <div className="card my-2">
      <div className="card-content">
        <div className="columns">
          <div className="column has-text-left">
            <StaleListNotice
              loadFailed={exerciseContext.exercisesLoadFailed}
              hasEntries={exerciseContext.exercisesInAssignment.length > 0}
            />
            {exerciseContext.exercisesInAssignment.length > 0 && getExerciseTable()}
            {exerciseContext.exercisesInAssignment.length === 0 && getEmptyMessageExercises()}
          </div>
        </div>
      </div>
    </div>
  );

  function getStudentsTableItem(student: any) {
    let name = student.user.token;
    console.log(student);
    if (student.user.matriculationNumber) {
      name = student.user.givenName + " " + student.user.familyName + " (" + student.user.matriculationNumber + ")";
    }
    console.log(name);
    return (
      <tr key={"Student" + student.userId}>
        <th>{name}</th>
        <th>{student.submissionExercise.length}</th>
        <td>
          <button
            className="button is-success"
            onClick={() => {
              localStorage.setItem("currentUser", name);
              localStorage.setItem("currentUserId", student.userId);
              navigate("/submissions/student/" + id + "/" + student.userId);
            }}
          >
            {t("submissions")}
          </button>
        </td>
      </tr>
    );
  }

  function getStudentsTable() {
    return (
      <div className="table-container">
        <table className="table is-striped is-narrow is-hoverable is-fullwidth">
          <thead className="is-italic">
            <tr>
              <td>{t("name")}</td>
              <td>{t("numberOfSubmissions")}</td>
              <td>{t("options")}</td>
            </tr>
          </thead>
          <tbody>
            {submissionContext.submissions.map((student: any) => {
              return getStudentsTableItem(student);
            })}
          </tbody>
        </table>
      </div>
    );
  }

  function getEmptyMessageStudents() {
    if (submissionContext.submissionsLoadFailed) {
      return <div className="notification is-danger is-light">{t("listLoadFailed")}</div>;
    }
    return <p className="is-size-7">{t("noSubmissions")} </p>;
  }

  let renderStudentsTable = (
    <div className="card my-2">
      <div className="card-content">
        <div className="columns">
          <div className="column has-text-left">
            <StaleListNotice
              loadFailed={submissionContext.submissionsLoadFailed}
              hasEntries={submissionContext.submissions.length > 0}
            />
            {submissionContext.submissions.length > 0 && getStudentsTable()}
            {submissionContext.submissions.length === 0 && getEmptyMessageStudents()}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="section">
      <div className="card mt-6">
        <header className="card-header">
          <p className="card-header-title">
            {t("assessment")}
            <Tooltip
              icon="circle-info"
              class="mt-3 icon tooltip no-border is-white has-tooltip-multiline"
              content={t("assessmentcardtooltip")}
            ></Tooltip>
          </p>
        </header>
        <div className="card-content">
          <div className="content">
            <div className="columns">
              <div className="column has-text-weight-light is-italic">{t("allExercisesInAssignment")}</div>
            </div>
            {renderExerciseTable}
          </div>
        </div>
      </div>

      <div className="card mt-6">
        <header className="card-header">
          <p className="card-header-title">
            {t("students")}
            <Tooltip
              icon="circle-info"
              class="mt-3 icon tooltip no-border is-white has-tooltip-multiline"
              content={t("studentcardtooltip")}
            ></Tooltip>
          </p>
        </header>
        <div className="card-content">
          <div className="content">
            <div className="columns">
              <div className="column has-text-weight-light is-italic">{t("studentSubmissionsForAssignment")}</div>
            </div>
            {renderStudentsTable}
          </div>
        </div>
      </div>
    </div>
  );
};
